import { isClient, isRSC, isServer, isWeb } from "@tamagui/constants";
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { getConfig } from "../config";
import { isDevTools } from "../constants/isDevTools";
import { createProxy } from "../helpers/createProxy";
import {
  ThemeManager,
  getNonComponentParentManager,
  hasNoThemeUpdatingProps
} from "../helpers/ThemeManager";
import { ThemeManagerContext } from "../helpers/ThemeManagerContext";
import { GetThemeUnwrapped } from "./getThemeUnwrapped";
import { useServerRef } from "./useServerHooks";
const emptyProps = { name: null };
function getDefaultThemeProxied() {
  const config = getConfig();
  const name = Object.keys(config.themes)[0];
  return getThemeProxied({
    theme: config.themes[name],
    name
  });
}
const useTheme = (props = emptyProps) => {
  var _a;
  return (isRSC ? null : (_a = useThemeWithState(props)) == null ? void 0 : _a.theme) || getDefaultThemeProxied();
};
const useThemeWithState = (props) => {
  const keys = useServerRef([]);
  const changedTheme = useChangeThemeEffect(
    props,
    false,
    keys.current,
    isClient ? () => {
      var _a;
      return ((_a = props.shouldUpdate) == null ? void 0 : _a.call(props)) ?? keys.current.length === 0;
    } : void 0
  );
  const { themeManager, theme, name, className } = changedTheme;
  if (process.env.NODE_ENV === "development" && props.debug === "verbose") {
    console.groupCollapsed("  \u{1F539} useTheme =>", name);
    const logs = { ...props, name, className, ...isDevTools && { theme } };
    for (const key in logs) {
      console.log("  ", key, logs[key]);
    }
    console.groupEnd();
  }
  if (!changedTheme.theme) {
    if (process.env.NODE_ENV === "development") {
      console.warn("No theme found", name, props, themeManager);
    }
    return null;
  }
  const proxiedTheme = useMemo(() => {
    return getThemeProxied(changedTheme, keys.current);
  }, [theme, name, className, themeManager]);
  return {
    ...changedTheme,
    theme: proxiedTheme
  };
};
function getThemeProxied({
  theme,
  themeManager
}, keys) {
  return createProxy(theme, {
    has(_, key) {
      if (typeof key === "string") {
        if (key[0] === "$")
          key = key.slice(1);
        return themeManager == null ? void 0 : themeManager.allKeys.has(key);
      }
      return Reflect.has(theme, key);
    },
    get(_, key) {
      if (key === GetThemeUnwrapped) {
        return theme;
      }
      if (key === "__proto__" || key === "$typeof" || typeof key !== "string" || !themeManager) {
        return Reflect.get(_, key);
      }
      const keyString = key[0] === "$" ? key.slice(1) : key;
      const val = themeManager.getValue(keyString);
      if (val && keys) {
        return new Proxy(val, {
          // when they touch the actual value we only track it
          // if its a variable (web), its ignored!
          get(_2, subkey) {
            if (subkey === "get") {
              return () => val;
            }
            if (subkey === "val" && !keys.includes(keyString)) {
              keys.push(keyString);
            }
            return Reflect.get(val, subkey);
          }
        });
      }
      return val;
    }
  });
}
const activeThemeManagers = /* @__PURE__ */ new Set();
const useChangeThemeEffect = (props, root = false, keys, disableUpdate) => {
  if (isRSC) {
    return {
      isNewTheme: false,
      ...createState().state,
      themeManager: null
    };
  }
  const {
    // @ts-expect-error internal use only
    disable
  } = props;
  const ogParentManager = useContext(ThemeManagerContext);
  const hasThemeUpdatingProps = !hasNoThemeUpdatingProps(props);
  const parentManager = disable ? ogParentManager : getNonComponentParentManager(ogParentManager);
  if (disable) {
    if (!ogParentManager)
      throw `\u274C`;
    return {
      isNewTheme: false,
      ...ogParentManager.state,
      themeManager: ogParentManager
    };
  }
  const [themeState, setThemeState] = useState(createState);
  const { state, mounted, isNewTheme, themeManager } = themeState;
  const isInversingOnMount = Boolean(!themeState.mounted && props.inverse);
  function getShouldUpdateTheme(manager = themeManager, nextState, prevState = state, forceShouldChange = false) {
    const next = nextState || manager.getState(props, parentManager);
    if (!next)
      return;
    if ((disableUpdate == null ? void 0 : disableUpdate()) === true)
      return;
    if (!forceShouldChange && !manager.getStateShouldChange(next, prevState))
      return;
    return next;
  }
  if (!isServer) {
    useLayoutEffect(() => {
      if (props.inverse && !mounted) {
        setThemeState({ ...themeState, mounted: true });
        return;
      }
      if (isNewTheme && themeManager) {
        activeThemeManagers.add(themeManager);
      }
      const nextState = getShouldUpdateTheme(themeManager);
      if (nextState) {
        if (isNewTheme) {
          themeManager.updateState(nextState, true);
        }
        setThemeState(createState);
      } else {
        if (isNewTheme) {
          setThemeState(createState);
        }
      }
      const selfListenerDispose = themeManager.onChangeTheme((_a, _b, forced) => {
        if (forced) {
          setThemeState((prev) => createState(prev, true));
        }
      });
      const disposeChangeListener = parentManager == null ? void 0 : parentManager.onChangeTheme((name, manager) => {
        const shouldUpdate = Boolean((keys == null ? void 0 : keys.length) || isNewTheme);
        if (process.env.NODE_ENV === "development" && props.debug) {
          const logs = { shouldUpdate, props, name, manager, keys };
          console.log(` \u{1F538} onChange`, themeManager.id, logs);
        }
        if (shouldUpdate) {
          setThemeState(createState);
        }
      }, themeManager.id);
      return () => {
        selfListenerDispose();
        disposeChangeListener == null ? void 0 : disposeChangeListener();
        activeThemeManagers.delete(themeManager);
      };
    }, [
      parentManager,
      isNewTheme,
      props.componentName,
      props.inverse,
      props.name,
      props.reset,
      themeState.mounted
    ]);
    if (process.env.NODE_ENV === "development") {
      useEffect(() => {
        globalThis["TamaguiThemeManagers"] ??= /* @__PURE__ */ new Set();
        globalThis["TamaguiThemeManagers"].add(themeManager);
        return () => {
          globalThis["TamaguiThemeManagers"].delete(themeManager);
        };
      }, [themeManager]);
    }
  }
  if (isInversingOnMount) {
    if (!parentManager)
      throw "\u274C";
    return {
      isNewTheme: false,
      ...parentManager.state,
      className: "",
      themeManager: parentManager
    };
  }
  return {
    ...state,
    isNewTheme,
    themeManager
  };
  function createState(prev, force = false) {
    if (prev && (disableUpdate == null ? void 0 : disableUpdate())) {
      return prev;
    }
    let themeManager2 = parentManager;
    let state2;
    const getNewThemeManager = () => {
      return new ThemeManager(props, root ? "root" : parentManager);
    };
    if (hasThemeUpdatingProps) {
      if (prev == null ? void 0 : prev.themeManager) {
        themeManager2 = prev.themeManager;
        const forceChange = Boolean(keys == null ? void 0 : keys.length);
        const next = themeManager2.getState(props, parentManager);
        const nextState = getShouldUpdateTheme(
          themeManager2,
          next,
          prev.state,
          forceChange
        );
        if (nextState) {
          state2 = nextState;
          if (!prev.isNewTheme || !isWeb) {
            themeManager2 = getNewThemeManager();
          } else {
            themeManager2.updateState(nextState, true);
          }
        } else {
          if (prev.isNewTheme) {
            if (parentManager && !next) {
              themeManager2 = parentManager;
            }
          }
        }
      } else {
        themeManager2 = getNewThemeManager();
        state2 = { ...themeManager2.state };
      }
    }
    const isNewTheme2 = Boolean(themeManager2 !== parentManager);
    const mounted2 = !props.inverse ? true : root || (prev == null ? void 0 : prev.mounted);
    if (!state2) {
      if (isNewTheme2) {
        state2 = { ...themeManager2.state };
      } else {
        state2 = ogParentManager.state;
        themeManager2 = ogParentManager;
      }
    }
    if (!force && state2.name === (prev == null ? void 0 : prev.state.name)) {
      return prev;
    }
    const response = {
      state: state2,
      themeManager: themeManager2,
      isNewTheme: isNewTheme2,
      mounted: mounted2
    };
    if (process.env.NODE_ENV === "development" && props["debug"]) {
      console.groupCollapsed(` \u{1F537} ${themeManager2.id} useChangeThemeEffect createState`);
      const parentState = { ...parentManager == null ? void 0 : parentManager.state };
      const parentId = parentManager == null ? void 0 : parentManager.id;
      const themeManagerState = { ...themeManager2.state };
      console.log({
        props,
        parentState,
        parentId,
        themeManager: themeManager2,
        prev,
        response,
        themeManagerState
      });
      console.groupEnd();
    }
    return response;
  }
};
export {
  activeThemeManagers,
  getThemeProxied,
  useChangeThemeEffect,
  useTheme,
  useThemeWithState
};
//# sourceMappingURL=useTheme.js.map
